import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Globe, Link as LinkIcon, TrendingUp } from "lucide-react";
import api from "../../lib/api";

/**
 * PassportShareAnalytics — founder-facing analytics for the public passport
 * share link. Shows a small daily views chart + top referrers + country
 * breakdown for the last N days (default 14).
 */
export default function PassportShareAnalytics({ days = 14 }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/startup/passport/analytics?days=${days}`);
        setData(res.data);
      } catch (e) { /* silent */ }
    })();
  }, [days]);

  if (!data) return null;
  const maxViews = Math.max(1, ...data.daily.map((d) => d.views));

  return (
    <div className="border border-navy/10 bg-white rounded-lg mb-6" data-testid="passport-share-analytics">
      <div className="px-5 py-3 border-b border-navy/10 bg-navy/[0.02] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye size={12} className="text-emerald-700" />
          <div className="overline text-emerald-700">Passport Share Analytics</div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Last {days} days · {data.total_in_window} views
        </div>
      </div>

      <div className="p-5">
        {/* Daily views bars */}
        <div className="mb-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2">
            Daily views
          </div>
          <div className="flex items-end gap-1 h-24" data-testid="passport-analytics-bars">
            {data.daily.map((d) => (
              <div
                key={d.date}
                className="flex-1 flex flex-col items-center justify-end min-w-0"
                title={`${d.date}: ${d.views} views`}
              >
                <div
                  className="w-full bg-emerald-500 rounded-sm transition-all"
                  style={{ height: `${(d.views / maxViews) * 100}%`, minHeight: d.views ? 3 : 1 }}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[9px] font-mono text-ink-muted mt-1">
            <span>{data.daily[0]?.date?.slice(5)}</span>
            <span>{data.daily[data.daily.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <LinkIcon size={11} className="text-ink-muted" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Top referrers</div>
            </div>
            <ul className="space-y-1 text-xs">
              {data.top_referrers.length === 0 && <li className="text-ink-muted italic">None yet.</li>}
              {data.top_referrers.map((r) => (
                <li key={r.host} className="flex items-center justify-between" data-testid={`referrer-${r.host}`}>
                  <span className="text-ink truncate">{r.host}</span>
                  <span className="font-mono text-ink-muted ml-2">{r.views}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Globe size={11} className="text-ink-muted" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Top countries</div>
            </div>
            <ul className="space-y-1 text-xs">
              {data.top_countries.length === 0 && <li className="text-ink-muted italic">Not enough signal.</li>}
              {data.top_countries.map((c) => (
                <li key={c.country} className="flex items-center justify-between" data-testid={`country-${c.country}`}>
                  <span className="text-ink">{c.country}</span>
                  <span className="font-mono text-ink-muted ml-2">{c.views}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
